import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import LoadingIndicator from "./LoadingIndicator";
import { structuredDataManager } from "../utils/StructuredDataManager";
import { useSidebarState } from "../hooks/useSidebarState";
import { sidebarStateManager } from "../utils/SidebarStateManager";
import { CloseIcon, TrendingUpIcon, TrendingDownIcon } from "./Icon";
import styles from "./RestaurantDetailsSidebar.module.css";
import { SidebarType } from "../utils/SidebarStateManager";
import { useSidebarOpenEffect } from "../hooks/useSidebarOpenEffect";
import RestoApiService, { TrendChange } from "../utils/RestoApiService";
import AnalyticsManager from "../utils/AnalyticsManager";
import { generateRestoSlug } from "../utils/RestoSlugUtils";

interface TrendItemProps {
  trend: TrendChange;
  type: 'upward' | 'downward';
  onSelectRestaurant: (restaurantId: number, trend: TrendChange, type: 'upward' | 'downward') => void;
}

const TrendItemComponent: React.FC<TrendItemProps> = ({ trend, type, onSelectRestaurant }) => {
  const { t } = useTranslation();
  
  const getScoreEmoji = (score: number) => {
    if (score === 5) return "😍";
    if (score === 4) return "😃";
    if (score === 3) return "🙂";
    if (score === 2) return "🤮";
    if (score === 1) return "💀";
    return "❓";
  };

  // Calculate days since last inspection
  const daysSinceInspection = Math.floor(
    (new Date().getTime() - new Date(trend.currentDate).getTime()) / (1000 * 60 * 60 * 24)
  );
  
  // Get inspection recency indicator
  const getRecencyColor = () => {
    if (daysSinceInspection <= 30) return 'text-green-400';
    if (daysSinceInspection <= 90) return 'text-yellow-400';
    return 'text-gray-400';
  };

  const isSignificant = Math.abs(trend.changeAmount) >= 2;

  return (
    <div 
      className={`group cursor-pointer transition-all duration-200 ${
        isSignificant
          ? 'bg-white dark:bg-gray-800/70 border border-gray-200 dark:border-gray-700/60 shadow-sm rounded-xl p-3.5 hover:shadow-md'
          : 'bg-transparent border-b border-gray-200 dark:border-gray-700/40 rounded-none p-3.5 hover:bg-gray-100/60 dark:hover:bg-gray-800/40'
      }`}
      onClick={() => onSelectRestaurant(trend.restaurantId, trend, type)}
    >
      <div className="flex items-start justify-between gap-3">
        {/* Left: Restaurant info and location */}
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
            {trend.restaurantName}
          </h4>
          <p className="text-xs text-gray-500 truncate mt-0.5">
            {trend.restaurantAddress.split(',')[0]}
          </p>
        </div>
        
        {/* Right: Score change visualization */}
        <div className="flex flex-col items-end gap-1">
          {/* Emoji transition */}
          <div className="flex items-center gap-1">
            <span className="text-xl">{getScoreEmoji(trend.previousScore)}</span>
            <span className={`text-sm ${type === 'upward' ? 'text-green-400' : 'text-red-400'}`}>
              →
            </span>
            <span className="text-xl">{getScoreEmoji(trend.currentScore)}</span>
          </div>
          
          {/* Change indicator and recency */}
          <div className="flex items-center gap-2 text-xs">
            <span className={`font-bold text-xs px-1.5 py-0.5 rounded-full ${
              type === 'upward'
                ? 'text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/40'
                : 'text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40'
            }`}>
              {type === 'upward' ? '+' : ''}{trend.changeAmount}
            </span>
            <span 
              className={getRecencyColor()} 
              title={`${daysSinceInspection} ${t('trends.daysAgo')}`}
            >
              •
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

interface TrendsContentProps {
  citySlug?: string;
  initialUpward?: TrendChange[];
  initialDownward?: TrendChange[];
}

const TrendsContent: React.FC<TrendsContentProps> = ({ citySlug, initialUpward, initialDownward }) => {
  const { t, i18n } = useTranslation();
  const hasInitialData = !!(initialUpward?.length || initialDownward?.length);
  const [upwardTrends, setUpwardTrends] = useState<TrendChange[]>(initialUpward ?? []);
  const [downwardTrends, setDownwardTrends] = useState<TrendChange[]>(initialDownward ?? []);
  const [loading, setLoading] = useState(!hasInitialData);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'upward' | 'downward'>('upward');
  const [searchQuery, setSearchQuery] = useState('');
  const [minChangeFilter, setMinChangeFilter] = useState<number>(0);
  const [sortBy, setSortBy] = useState<'change' | 'name' | 'recent'>('change');
  // On city pages, trends are filtered server-side to that city; the empty
  // state offers a switch to the national list. Tracking WHICH city the user
  // switched away from makes the opt-out reset itself on city change.
  const [nationwideForCity, setNationwideForCity] = useState<string | undefined>(undefined);
  const { closeSidebar } = useSidebarState(sidebarStateManager);
  const restoApiService = useMemo(() => new RestoApiService(), []);

  const showNationwide = !!citySlug && nationwideForCity === citySlug;
  const effectiveCitySlug = showNationwide ? undefined : citySlug;
  // Same display convention as CityStatsContent's titleCase
  const cityDisplayName = (citySlug ?? '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const fetchTrends = async () => {
    setLoading(true);
    setError(null);

    try {
      const [upwardResult, downwardResult] = await Promise.all([
        restoApiService.fetchUpwardTrends(400, effectiveCitySlug),
        restoApiService.fetchDownwardTrends(400, effectiveCitySlug)
      ]);

      if (upwardResult.success && upwardResult.data) {
        setUpwardTrends(upwardResult.data.trends);
      }

      if (downwardResult.success && downwardResult.data) {
        setDownwardTrends(downwardResult.data.trends);
      }

      if (!upwardResult.success && !downwardResult.success) {
        setError(t('trends.error'));
      }

      // Track successful data load
      if ((upwardResult.success || downwardResult.success) && (upwardResult.data || downwardResult.data)) {
        const analytics = AnalyticsManager.getInstance();
        analytics.trackEvent('trends_data_loaded', {
          upward_count: upwardResult.data?.trends.length || 0,
          downward_count: downwardResult.data?.trends.length || 0,
          total_count: (upwardResult.data?.trends.length || 0) + (downwardResult.data?.trends.length || 0),
          city: effectiveCitySlug || 'nationwide',
          event_category: 'trends'
        });
      }
    } catch (err) {
      setError(t('trends.error'));
      // Track error
      const analytics = AnalyticsManager.getInstance();
      analytics.trackEvent('trends_data_error', {
        error_message: err instanceof Error ? err.message : 'Unknown error',
        event_category: 'trends'
      });
    } finally {
      setLoading(false);
    }
  };

  // Refetch trends when sidebar opens
  useSidebarOpenEffect(SidebarType.TRENDS, fetchTrends);

  // Initial fetch + refetch when the city filter changes — skip only the
  // first fetch when SSR already seeded the trends (nationwide /trending)
  useEffect(() => {
    if (!hasInitialData || effectiveCitySlug) fetchTrends();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCitySlug]);

  // Add structured data for trends
  useEffect(() => {
    const allTrends = [...upwardTrends, ...downwardTrends];
    if (allTrends.length > 0) {
      const trendsSchema = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "Restaurant Hygiene Score Trends in Belgium",
        "description": `Latest hygiene score changes: ${upwardTrends.length} improving and ${downwardTrends.length} declining restaurants`,
        "numberOfItems": allTrends.length,
        "itemListElement": allTrends.slice(0, 10).map((trend, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Restaurant",
            "name": trend.restaurantName,
            "address": trend.restaurantAddress
          }
        }))
      };

      structuredDataManager.addStructuredData('restaurantTrends', trendsSchema);
    }

    return () => {
      structuredDataManager.removeStructuredData('restaurantTrends');
    };
  }, [upwardTrends, downwardTrends]);

  const handleRetry = () => {
    fetchTrends();
  };

  const handleSelectRestaurant = (restaurantId: number, trend: TrendChange, type: 'upward' | 'downward') => {
    // Track the trend click with more details
    const analytics = AnalyticsManager.getInstance();
    analytics.trackEvent('trend_restaurant_select', {
      restaurant_id: restaurantId,
      restaurant_name: trend.restaurantName,
      trend_type: type,
      change_amount: trend.changeAmount,
      previous_score: trend.previousScore,
      current_score: trend.currentScore,
      days_since_inspection: Math.floor((new Date().getTime() - new Date(trend.currentDate).getTime()) / (1000 * 60 * 60 * 24)),
      event_category: 'trends'
    });
    
    // Close the trends sidebar
    closeSidebar();
    
    // Navigate to the restaurant with SEO-friendly slug
    const restoSlug = generateRestoSlug(trend.restaurantName, restaurantId);
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('restoId', restoSlug);
    const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    window.history.pushState({}, '', newUrl);
    
    // Dispatch a custom event to notify the app about the selection
    window.dispatchEvent(new CustomEvent('restaurantSelected', { 
      detail: { 
        restaurantId: restaurantId.toString(),
        openSidebar: true
      } 
    }));
  };

  // Filter and sort trends (city filtering happens server-side)
  const processedUpwardTrends = useMemo(() => {
    let filtered = upwardTrends;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(trend =>
        trend.restaurantName.toLowerCase().includes(query) ||
        trend.restaurantAddress.toLowerCase().includes(query)
      );
    }

    // Apply minimum change filter
    if (minChangeFilter > 0) {
      filtered = filtered.filter(trend => Math.abs(trend.changeAmount) >= minChangeFilter);
    }

    // Apply sorting
    const sorted = [...filtered];
    switch (sortBy) {
      case 'change':
        sorted.sort((a, b) => b.changeAmount - a.changeAmount);
        break;
      case 'name':
        sorted.sort((a, b) => a.restaurantName.localeCompare(b.restaurantName));
        break;
      case 'recent':
        sorted.sort((a, b) => new Date(b.currentDate).getTime() - new Date(a.currentDate).getTime());
        break;
    }

    return sorted;
  }, [upwardTrends, searchQuery, minChangeFilter, sortBy]);

  const processedDownwardTrends = useMemo(() => {
    let filtered = downwardTrends;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(trend =>
        trend.restaurantName.toLowerCase().includes(query) ||
        trend.restaurantAddress.toLowerCase().includes(query)
      );
    }

    // Apply minimum change filter
    if (minChangeFilter > 0) {
      filtered = filtered.filter(trend => Math.abs(trend.changeAmount) >= minChangeFilter);
    }

    // Apply sorting
    const sorted = [...filtered];
    switch (sortBy) {
      case 'change':
        sorted.sort((a, b) => a.changeAmount - b.changeAmount);
        break;
      case 'name':
        sorted.sort((a, b) => a.restaurantName.localeCompare(b.restaurantName));
        break;
      case 'recent':
        sorted.sort((a, b) => new Date(b.currentDate).getTime() - new Date(a.currentDate).getTime());
        break;
    }
    
    return sorted;
  }, [downwardTrends, searchQuery, minChangeFilter, sortBy]);

  return (
    <div className="flex flex-col h-full">
      {/* Main scrollable content area */}
      <div className={`flex-grow overflow-y-auto overflow-x-hidden ${styles.customScrollbar}`} id="sidebar-scroll-container">
        <div className="pl-4 pr-2 pt-4" id="sidebar-content">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <LoadingIndicator isLoading={loading} />
            </div>
          )}
          
          {error && !loading && (
            <div className="text-center py-12">
              <p className="text-sm text-gray-400 mb-4">{error}</p>
              <button
                onClick={handleRetry}
                className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 underline"
              >
                {t('common.tryAgain')}
              </button>
            </div>
          )}
          
          {!loading && !error && upwardTrends.length === 0 && downwardTrends.length === 0 && (
            <div className={`${styles.card} smooth-transition fade-in`}>
              <div className="flex flex-col items-center justify-center py-8">
                <TrendingUpIcon className="h-12 w-12 text-gray-500 mb-4" />
                <div className="text-center">
                  {effectiveCitySlug ? (
                    <>
                      <p className="text-sm text-gray-500 dark:text-gray-300 font-medium">
                        {t('trends.cityNoTrends', { city: cityDisplayName })}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        {t('trends.cityNoTrendsSubtext')}
                      </p>
                      <button
                        onClick={() => setNationwideForCity(citySlug)}
                        className="mt-4 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded-lg transition-colors"
                      >
                        {t('trends.showNationalTrends')}
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-gray-500 dark:text-gray-300 font-medium">
                        {t('trends.noTrends')}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        {t('trends.noTrendsSubtext')}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {!loading && !error && (upwardTrends.length > 0 || downwardTrends.length > 0) && (
            <>
              {/* Header with close button */}
              <div className="mb-4">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <TrendingUpIcon className="h-5 w-5 text-blue-400" />
                    <h3 className="text-base font-medium text-gray-700 dark:text-gray-200">
                      {t('trends.title')}
                    </h3>
                  </div>
                  <button
                    onClick={closeSidebar}
                    className="p-2 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg transition-all duration-200"
                    aria-label="Close"
                  >
                    <CloseIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Search bar */}
              <div className="mt-4 space-y-2">
                <input
                  type="text"
                  placeholder={t('common.search')}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    // Track search usage
                    if (e.target.value.length > 2) {
                      const analytics = AnalyticsManager.getInstance();
                      analytics.trackEvent('trends_search', {
                        search_query: e.target.value,
                        results_count: processedUpwardTrends.length + processedDownwardTrends.length,
                        event_category: 'trends'
                      });
                    }
                  }}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-colors shadow-sm"
                />
                
                {/* Quick filters */}
                <div className="flex gap-2 text-xs">
                  <button
                    onClick={() => {
                      const newValue = minChangeFilter === 2 ? 0 : 2;
                      setMinChangeFilter(newValue);
                      // Track filter usage
                      const analytics = AnalyticsManager.getInstance();
                      analytics.trackEvent('trends_filter_toggle', {
                        filter_type: 'big_changes',
                        filter_value: newValue,
                        enabled: newValue === 2,
                        event_category: 'trends'
                      });
                    }}
                    className={`px-3 py-1 rounded-full transition-colors ${
                      minChangeFilter === 2
                        ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50'
                        : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:text-gray-800 dark:hover:text-gray-100 hover:border-gray-400 shadow-sm'
                    }`}
                  >
                    {t('trends.bigChanges')}
                  </button>
                  <button
                    onClick={() => {
                      const newValue = sortBy === 'recent' ? 'change' : 'recent';
                      setSortBy(newValue);
                      // Track sort usage
                      const analytics = AnalyticsManager.getInstance();
                      analytics.trackEvent('trends_sort_toggle', {
                        sort_type: newValue,
                        event_category: 'trends'
                      });
                    }}
                    className={`px-3 py-1 rounded-full transition-colors ${
                      sortBy === 'recent'
                        ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50'
                        : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:text-gray-800 dark:hover:text-gray-100 hover:border-gray-400 shadow-sm'
                    }`}
                  >
                    {t('trends.mostRecent')}
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="mt-3">
                <div className="flex gap-2 bg-gray-200/70 dark:bg-gray-800 rounded-lg p-1 border border-gray-300/50 dark:border-gray-700">
                  <button
                    onClick={() => {
                      setActiveTab('upward');
                      // Track tab change
                      const analytics = AnalyticsManager.getInstance();
                      analytics.trackEvent('trends_tab_change', {
                        tab: 'upward',
                        count: processedUpwardTrends.length,
                        event_category: 'trends'
                      });
                    }}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md transition-colors ${
                      activeTab === 'upward'
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700/50 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-white/60 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <TrendingUpIcon className="h-4 w-4" />
                    <span className="text-sm font-medium">
                      {t('trends.improving')} ({processedUpwardTrends.length})
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab('downward');
                      // Track tab change
                      const analytics = AnalyticsManager.getInstance();
                      analytics.trackEvent('trends_tab_change', {
                        tab: 'downward',
                        count: processedDownwardTrends.length,
                        event_category: 'trends'
                      });
                    }}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md transition-colors ${
                      activeTab === 'downward'
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-300 dark:border-red-700/50 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-white/60 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <TrendingDownIcon className="h-4 w-4" />
                    <span className="text-sm font-medium">
                      {t('trends.declining')} ({processedDownwardTrends.length})
                    </span>
                  </button>
                </div>
              </div>

              {/* Tab content */}
              <div className="mt-4">
                {activeTab === 'upward' && upwardTrends.length > 0 && (
                  <>
                    {/* Insights */}
                    {processedUpwardTrends.length > 0 && (
                      <div className="mb-4 grid grid-cols-2 gap-2">
                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-800/40 rounded-lg p-3 shadow-sm">
                          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                            {processedUpwardTrends.filter(t => t.changeAmount >= 2).length}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {t('trends.significantChanges')}
                          </div>
                        </div>
                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-800/40 rounded-lg p-3 shadow-sm">
                          <div className="text-2xl font-bold text-gray-700 dark:text-gray-200">
                            {processedUpwardTrends.filter(t => {
                              const days = Math.floor((new Date().getTime() - new Date(t.currentDate).getTime()) / (1000 * 60 * 60 * 24));
                              return days <= 30;
                            }).length}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {t('trends.recentInspections')}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 pb-2 border-b border-gray-200 dark:border-gray-700/40">{t('trends.restaurants', 'Restaurants')}</div>
                    <div className="space-y-1 overflow-y-auto pr-2">
                      {processedUpwardTrends.length > 0 ? (
                        processedUpwardTrends.map((trend, index) => (
                            <TrendItemComponent 
                              key={`upward-${trend.restaurantId}-${index}`} 
                              trend={trend}
                              type="upward"
                              onSelectRestaurant={handleSelectRestaurant}
                            />
                          ))
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-sm text-gray-400">{t('common.noResults')}</p>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {activeTab === 'downward' && downwardTrends.length > 0 && (
                  <>
                    {/* Insights */}
                    {processedDownwardTrends.length > 0 && (
                      <div className="mb-4 grid grid-cols-2 gap-2">
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800/40 rounded-lg p-3 shadow-sm">
                          <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                            {processedDownwardTrends.filter(t => Math.abs(t.changeAmount) >= 2).length}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {t('trends.significantChanges')}
                          </div>
                        </div>
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800/40 rounded-lg p-3 shadow-sm">
                          <div className="text-2xl font-bold text-gray-700 dark:text-gray-200">
                            {processedDownwardTrends.filter(t => {
                              const days = Math.floor((new Date().getTime() - new Date(t.currentDate).getTime()) / (1000 * 60 * 60 * 24));
                              return days <= 30;
                            }).length}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {t('trends.recentInspections')}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 pb-2 border-b border-gray-200 dark:border-gray-700/40">{t('trends.restaurants', 'Restaurants')}</div>
                    <div className="space-y-1 overflow-y-auto pr-2">
                      {processedDownwardTrends.length > 0 ? (
                        processedDownwardTrends.map((trend, index) => (
                            <TrendItemComponent 
                              key={`downward-${trend.restaurantId}-${index}`} 
                              trend={trend}
                              type="downward"
                              onSelectRestaurant={handleSelectRestaurant}
                            />
                          ))
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-sm text-gray-400">{t('common.noResults')}</p>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {((activeTab === 'upward' && upwardTrends.length === 0) || 
                  (activeTab === 'downward' && downwardTrends.length === 0)) && (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-400">
                      {activeTab === 'upward' 
                        ? t('trends.noUpwardTrends')
                        : t('trends.noDownwardTrends')}
                    </p>
                  </div>
                )}
              </div>

              {/* View all link */}
              <div className="mt-6 text-center pb-4">
                <a
                  href={`/${i18n.language || 'en'}/trending`}
                  className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium hover:underline transition-colors"
                >
                  {t('trends.viewAll', 'View all trends')} &rarr;
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrendsContent;